import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Loader2, AlertCircle } from 'lucide-react'
import { apiService } from '@/services/api'
import { toast } from 'sonner'
import { useDrawerMessaging } from '@/hooks/useDrawerMessaging'
import { CategoryColorPicker } from './CategoryColorPicker'
import { generateSlug, validateSlug } from '@/utils/slugGenerator'

interface BlogCategoryFormProps {
  category?: {
    id: string
    name: string
    slug: string
    description?: string
    color?: string
  }
  onSuccess?: () => void
  onClose?: () => void
}

export const BlogCategoryForm: React.FC<BlogCategoryFormProps> = ({ category, onSuccess, onClose }) => {
  const { sendMessage } = useDrawerMessaging('blog-category-form')
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isValidating, setIsValidating] = useState(false)
  const maxDescriptionLength = 200

  // Form state
  const [formData, setFormData] = useState({
    name: category?.name || '',
    slug: category?.slug || '',
    description: category?.description || '',
    color: category?.color || '#3B82F6',
  })

  // Track form changes
  useEffect(() => {
    if (category) {
      // Check if form has changes compared to original category
      // This can be used in the future for dirty state tracking
      // const hasChanges = formData.name !== category.name || ...
    } else {
      // Check if form has any values for new category
      // This can be used in the future for dirty state tracking
      // const hasChanges = formData.name !== '' || ...
    }
  }, [formData, category])

  // Validate slug uniqueness (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (formData.slug && formData.slug !== category?.slug) {
        setIsValidating(true)
        try {
          // Check if slug is valid format
          if (!validateSlug(formData.slug)) {
            setErrors(prev => ({ ...prev, slug: 'Invalid slug format. Use only lowercase letters, numbers, and hyphens.' }))
          } else {
            // Here you would normally check uniqueness via API
            // For now, just clear the error
            setErrors(prev => {
              const newErrors = { ...prev }
              delete newErrors.slug
              return newErrors
            })
          }
        } finally {
          setIsValidating(false)
        }
      }
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [formData.slug, category?.slug])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      toast.error('Name is required')
      return
    }

    setLoading(true)

    try {
      const payload = {
        ...formData,
        slug: formData.slug || generateSlug(formData.name),
      }

      if (category) {
        await apiService.blog.categories.update(category.id, payload)
        toast.success('Category updated successfully')
      } else {
        await apiService.blog.categories.create(payload)
        toast.success('Category created successfully')
      }

      sendMessage({ to: 'blog-categories-page', action: 'refresh' })
      onSuccess?.()
    } catch (err) {
      console.error('Failed to save category:', err)
      toast.error('Failed to save category')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name */}
      <div>
        <Label htmlFor="name">Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => {
            const newName = e.target.value
            setFormData(prev => {
              const newData = { ...prev, name: newName }
              // Auto-generate slug if it's a new category and slug hasn't been manually edited
              if (!category && !prev.slug) {
                newData.slug = generateSlug(newName)
              }
              return newData
            })
          }}
          placeholder="Enter category name"
          disabled={loading}
          className={errors.name ? 'border-red-500' : ''}
        />
        {errors.name && (
          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {errors.name}
          </p>
        )}
      </div>

      {/* Slug */}
      <div>
        <Label htmlFor="slug">
          Slug
          {isValidating && (
            <span className="ml-2 text-xs text-muted-foreground">Validating...</span>
          )}
        </Label>
        <Input
          id="slug"
          value={formData.slug}
          onChange={(e) => {
            const newSlug = e.target.value.toLowerCase()
            setFormData({ ...formData, slug: newSlug })
          }}
          placeholder="category-url-slug"
          disabled={loading}
          className={errors.slug ? 'border-red-500' : ''}
        />
        <p className="text-xs text-muted-foreground mt-1">
          Leave empty to auto-generate from name
        </p>
        {errors.slug && (
          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {errors.slug}
          </p>
        )}
      </div>

      {/* Description */}
      <div>
        <div className="flex justify-between items-center mb-1">
          <Label htmlFor="description">Description</Label>
          <span className={`text-xs ${
            formData.description.length > maxDescriptionLength ? 'text-red-500' : 'text-muted-foreground'
          }`}>
            {formData.description.length}/{maxDescriptionLength}
          </span>
        </div>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => {
            const newDescription = e.target.value
            if (newDescription.length <= maxDescriptionLength) {
              setFormData({ ...formData, description: newDescription })
              if (errors.description) {
                setErrors(prev => {
                  const newErrors = { ...prev }
                  delete newErrors.description
                  return newErrors
                })
              }
            } else {
              setErrors(prev => ({ ...prev, description: 'Description is too long' }))
            }
          }}
          placeholder="Brief description of the category"
          rows={3}
          disabled={loading}
          className={errors.description ? 'border-red-500' : ''}
        />
        {errors.description && (
          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {errors.description}
          </p>
        )}
      </div>

      {/* Color Picker */}
      <CategoryColorPicker
        color={formData.color}
        onChange={(color) => setFormData({ ...formData, color })}
        disabled={loading}
      />

      {/* Form Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={onClose || onSuccess}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {category ? 'Update Category' : 'Create Category'}
        </Button>
      </div>
    </form>
  )
}